<template>
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <!-- Month grid -->
    <div class="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-3 md:p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <Button icon="pi pi-angle-right" text rounded size="small" @click="shiftMonth(-1)" />
        <button type="button" class="text-base font-semibold text-gray-900" @click="goToday">
          {{ currentMonthLabel }}
        </button>
        <Button icon="pi pi-angle-left" text rounded size="small" @click="shiftMonth(1)" />
      </div>

      <div class="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 font-medium mb-1.5">
        <div v-for="d of dayHeaders" :key="d">{{ d }}</div>
      </div>
      <div class="grid grid-cols-7 gap-1">
        <button
          v-for="cell of calendarCells"
          :key="cell.iso"
          type="button"
          :class="dayButtonClass(cell)"
          @click="selectedDayIso = cell.iso"
        >
          <span class="text-xs md:text-sm font-medium">{{ cell.day }}</span>
          <div v-if="cell.tasks.length" class="w-full mt-1 space-y-0.5 overflow-hidden">
            <div
              v-for="(t, i) of cell.tasks.slice(0, 2)"
              :key="i"
              class="text-[9px] md:text-[10px] truncate leading-tight rounded px-0.5"
              :class="
                t.completed
                  ? 'text-gray-400 line-through'
                  : isPast(cell.date)
                    ? 'text-red-600 bg-red-50'
                    : 'text-primary-700 bg-primary-50'
              "
            >
              {{ t.title }}
            </div>
            <div v-if="cell.tasks.length > 2" class="text-[9px] text-gray-400 text-center leading-tight">
              +{{ cell.tasks.length - 2 }}
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- Selected day -->
    <div class="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">{{ selectedDayLabel }}</h3>

      <div v-if="!selectedDayTasks.length" class="text-xs text-gray-400 text-center py-6">
        אין משימות ליום זה
      </div>
      <div v-else class="space-y-2">
        <TaskRow
          v-for="t of selectedDayTasks"
          :key="t._id"
          :task="t"
          show-time
          @toggle="emit('toggle', t, $event)"
          @open="emit('open-lead', t)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import type { IPlatformReminder } from '@torim-app/types';
import TaskRow from './TaskRow.vue';

const props = defineProps<{ tasks: IPlatformReminder[] }>();

const emit = defineEmits<{
  (e: 'toggle', task: IPlatformReminder, completed: boolean): void;
  (e: 'open-lead', task: IPlatformReminder): void;
}>();

const dayHeaders = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

const calendarCursor = ref(DateTime.local().startOf('month'));
const selectedDayIso = ref(DateTime.local().toISODate() as string);

const currentMonthLabel = computed(() => calendarCursor.value.setLocale('he').toFormat('LLLL yyyy'));

interface CalendarCell {
  iso: string;
  date: Date;
  day: number;
  inMonth: boolean;
  tasks: IPlatformReminder[];
}

const calendarCells = computed<CalendarCell[]>(() => {
  const firstOfMonth = calendarCursor.value;
  // Luxon weeks start on Monday; the Hebrew calendar grid starts on Sunday
  const gridStart = firstOfMonth.minus({ days: firstOfMonth.weekday % 7 });

  const byDay = new Map<string, IPlatformReminder[]>();
  for (const t of props.tasks) {
    const iso = DateTime.fromJSDate(new Date(t.dueDate)).toISODate();
    if (!iso) continue;
    if (!byDay.has(iso)) byDay.set(iso, []);
    byDay.get(iso)?.push(t);
  }

  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = gridStart.plus({ days: i });
    const iso = d.toISODate() as string;
    cells.push({
      iso,
      date: d.toJSDate(),
      day: d.day,
      inMonth: d.month === calendarCursor.value.month,
      tasks: (byDay.get(iso) || [])
        .slice()
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
    });
  }
  return cells;
});

const isPast = (date: Date) => DateTime.fromJSDate(date) < DateTime.local().startOf('day');

const dayButtonClass = (cell: CalendarCell) => {
  const classes = [
    'flex flex-col items-center justify-start py-1.5 px-1 min-h-[58px] md:min-h-[76px] rounded-lg border transition-all text-right',
  ];
  if (cell.iso === selectedDayIso.value) classes.push('border-primary-500 bg-primary-50');
  else if (cell.inMonth) classes.push('border-gray-100 hover:border-primary-200 hover:bg-gray-50');
  else classes.push('border-transparent text-gray-300');

  const today = DateTime.local().toISODate();
  if (cell.iso === today && cell.iso !== selectedDayIso.value) classes.push('ring-1 ring-primary-300');
  return classes;
};

const selectedDayTasks = computed(() =>
  props.tasks
    .filter((t) => DateTime.fromJSDate(new Date(t.dueDate)).toISODate() === selectedDayIso.value)
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
);

const selectedDayLabel = computed(() =>
  DateTime.fromISO(selectedDayIso.value).setLocale('he').toFormat('EEEE, dd/MM/yyyy'),
);

const shiftMonth = (delta: number) => {
  calendarCursor.value = calendarCursor.value.plus({ months: delta });
};

const goToday = () => {
  calendarCursor.value = DateTime.local().startOf('month');
  selectedDayIso.value = DateTime.local().toISODate() as string;
};
</script>
